import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import { useAppState } from '../context/AppStateContext';
import './City.css';
import CityJail from '../components/CityJail';
// Import the map image
import mapImage from '../images/map.png';

const City = () => {
  const [cityData, setCityData] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [ownerData, setOwnerData] = useState(null);
  const [loadingOwner, setLoadingOwner] = useState(false);
  const [activeBuilding, setActiveBuilding] = useState(null);
  // Update mapBackground to use the imported image by default
  const [mapBackground, setMapBackground] = useState(mapImage);
  
  const { character, inJail } = useAppState();

  // Fetch city information when component mounts
  useEffect(() => {
    const fetchCityData = async () => {
      try {
        setLoading(true);
        
        // First check if we already have character data with location
        let cityId = null;
        
        if (character && character.location) {
          if (typeof character.location === 'object' && character.location.id) {
            cityId = character.location.id;
          } else if (typeof character.location === 'string') {
            cityId = character.location;
          }
        }
        
        // If no city ID found in character data, fetch character data
        if (!cityId) {
          console.log('No location in character data, fetching character info');
          const characterResponse = await fetch(apiConfig.getCharacter, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          if (!characterResponse.ok) {
            throw new Error('Failed to fetch character information');
          }
          
          const characterData = await characterResponse.json();
          
          if (characterData.location) {
            if (typeof characterData.location === 'object') {
              cityId = characterData.location.id;
            } else {
              cityId = characterData.location;
            }
          }
        }
        
        // If we still don't have a city ID, the character isn't in a city
        if (!cityId) {
          throw new Error('Your character is not currently in any city.');
        }
        
        console.log(`Fetching data for city ID: ${cityId}`);
        
        // Fetch city data using the ID
        const cityResponse = await fetch(apiConfig.city(cityId), {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!cityResponse.ok) {
          throw new Error(`Failed to fetch city data: ${cityResponse.status}`);
        }

        const data = await cityResponse.json();
        setCityData(data);
        console.log('City data loaded:', data);

        // Fetch owner data if there's an owner
        if (data.owner) {
          fetchOwnerData(data.owner);
        }

        // Now fetch all cities for the map
        await fetchAllCities();
      } catch (err) {
        console.error('Error fetching city data:', err);
        setError(err.message || 'Failed to load city information.');
      } finally {
        setLoading(false);
      }
    };

    fetchCityData();
  }, [character]);

  // Fetch all cities for the map
  const fetchAllCities = async () => {
    try {
      const response = await fetch(apiConfig.cities, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cities: ${response.status}`);
      }

      const citiesData = await response.json();
      console.log('All cities loaded for map:', citiesData.length);
      setAllCities(citiesData);
    } catch (err) {
      console.error('Error fetching all cities:', err);
      // Non-fatal error, don't set main error state
    }
  };

  // Fetch owner character data
  const fetchOwnerData = async (ownerId) => {
    try {
      setLoadingOwner(true);
      const response = await fetch(`${apiConfig.getCharacter}${ownerId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch owner data');
        return;
      }

      const data = await response.json();
      setOwnerData(data);
    } catch (err) {
      console.error('Error fetching owner data:', err);
    } finally {
      setLoadingOwner(false);
    }
  };

  // Get icon based on building type
  const getBuildingIcon = (type) => {
    // Handle numeric building types
    if (typeof type === 'number') {
      switch (type) {
        case 0: return '🏛️'; // City Hall
        case 1: return '⛪'; // Church
        case 2: return '⚰️'; // Graveyard
        case 3: return '🛒'; // Shop
        // Add more numeric types as needed
        default: return '🏢'; // Default building
      }
    }
    
    // Handle string building types (for backward compatibility)
    const typeString = type != null ? String(type) : '';
    
    switch (typeString.toLowerCase()) {
      case 'tavern': return '🍺';
      case 'blacksmith': return '⚒️';
      case 'temple': return '⛪';
      case 'market': return '🛒';
      case 'library': return '📚';
      case 'guild': return '🏛️';
      case 'guard': return '🛡️';
      case 'bank': return '💰';
      case 'stable': return '🐎';
      case 'housing': return '🏘️';
      case 'city hall': return '🏛️';
      case 'church': return '⛪';
      case 'graveyard': return '⚰️';
      case 'shop': return '🛒';
      default: return '🏢';
    }
  };

  // Helper function to get building name from type
  const getBuildingName = (type) => {
    if (typeof type === 'number') {
      switch (type) {
        case 0: return 'City Hall';
        case 1: return 'Church';
        case 2: return 'Graveyard';
        case 3: return 'Shop';
        default: return 'Building';
      }
    }
    
    return type || 'Building';
  };

  // Get city icon based on alignment
  const getCityIcon = (alignment) => {
    if (alignment === undefined || alignment === null) return '🏙️';
    if (alignment > 30) return '✨';
    if (alignment > 10) return '🏰';
    if (alignment > 0) return '🏘️';
    if (alignment === 0) return '🏙️'; // Explicitly handle 0
    if (alignment > -10) return '🏚️';
    if (alignment > -30) return '🏚️';
    return '🏴';
  };

  // Get alignment text
  const getAlignmentText = (alignment) => {
    if (!alignment && alignment !== 0) return 'Unknown';
    if (alignment > 30) return 'Virtuous';
    if (alignment > 10) return 'Good';
    if (alignment > 0) return 'Slightly Good';
    if (alignment === 0) return 'Neutral';
    if (alignment > -10) return 'Slightly Evil';
    if (alignment > -30) return 'Evil';
    return 'Wicked';
  };

  // Update map background
  const updateMapBackground = (imageUrl) => {
    setMapBackground(imageUrl);
  };

  // Add this function to help position the map background
  const adjustMapAlignment = () => {
    const mapContainer = document.querySelector('.map-grid-container');
    const mapBackground = document.querySelector('.map-background');
    const mapGrid = document.querySelector('.map-grid');
    
    if (mapContainer && mapBackground && mapGrid) {
      // Instead of using offsets, position the background to exactly match the container
      mapBackground.style.top = '0';
      mapBackground.style.left = '0';
      mapBackground.style.width = '100%';
      mapBackground.style.height = '100%';
      
      // Log dimensions for debugging
      console.log('Grid dimensions:', mapGrid.offsetWidth, 'x', mapGrid.offsetHeight);
      console.log('Background dimensions:', mapBackground.offsetWidth, 'x', mapBackground.offsetHeight);
    }
  };

  // Call the adjustment function when the component mounts and when the tab changes
  useEffect(() => {
    if (activeTab === 'overview') {
      // Adjust immediately
      adjustMapAlignment();
      
      // And again after a short delay to ensure everything has rendered and calculated
      const timer = setTimeout(() => {
        adjustMapAlignment();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, mapBackground]);

  // Also adjust alignment if window is resized
  useEffect(() => {
    const handleResize = () => {
      if (activeTab === 'overview') {
        adjustMapAlignment();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  if (loading) {
    return (
      <div className="page-content city-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading city information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content city-page">
        <div className="error-container">
          <h2>Cannot Display City</h2>
          <p>{error}</p>
          <Link to="/travel" className="action-button">Find a City</Link>
        </div>
      </div>
    );
  }

  if (!cityData) {
    return (
      <div className="page-content city-page">
        <div className="error-container">
          <h2>No City Found</h2>
          <p>You are not currently in a city.</p>
          <Link to="/travel" className="action-button">Find a City</Link>
        </div>
      </div>
    );
  }

  // Generate grid cells for the map with all cities
  const generateMapGrid = () => {
    // Filter out cities without valid coordinates
    const citiesWithCoords = allCities.filter(city => 
      typeof city.xPosition === 'number' && 
      typeof city.yPosition === 'number'
    );
    
    // Log cities with coordinates for debugging
    console.log('Cities with coordinates:', citiesWithCoords.length);
    
    // Add current city to the list
    let currentCityIncluded = false;
    if (cityData && 
        typeof cityData.xPosition === 'number' && 
        typeof cityData.yPosition === 'number') {
      
      const cityExists = citiesWithCoords.some(c => c.id === cityData.id);
      if (!cityExists) {
        citiesWithCoords.push(cityData);
        console.log('Added current city to map data');
      }
      currentCityIncluded = true;
      
      // Debug log to verify coordinates
      console.log('Current city position:', 
        cityData.xPosition, 
        cityData.yPosition
      );
    }
    
    // If we have no cities with coordinates, use the default grid
    if (citiesWithCoords.length === 0) {
      console.log('No cities with coordinates, generating default grid');
      return generateDefaultGrid();
    }
    
    // Set default grid size
    const defaultGridSize = 10;
    let gridWidth = defaultGridSize;
    let gridHeight = defaultGridSize;
    
    // Create a map of city positions for quick lookup
    const cityPositions = {};
    
    // Default to centered 10x10 grid if no coordinate data
    let minX = -5, maxX = 4, minY = -5, maxY = 4; 
    
    if (citiesWithCoords.length > 0) {
      // If we have cities with coordinates, recalculate the grid
      minX = Math.min(...citiesWithCoords.map(city => city.xPosition));
      maxX = Math.max(...citiesWithCoords.map(city => city.xPosition));
      minY = Math.min(...citiesWithCoords.map(city => city.yPosition));
      maxY = Math.max(...citiesWithCoords.map(city => city.yPosition));
      
      // Add padding - REMOVED Math.max(0, ...) to allow negative coordinates
      minX = minX - 1;
      minY = minY - 1;
      maxX = maxX + 1;
      maxY = maxY + 1;
      
      // Ensure minimum size
      if (maxX - minX < 9) maxX = minX + 9;
      if (maxY - minY < 9) maxY = minY + 9;
      
      // Get grid dimensions
      gridWidth = maxX - minX + 1;
      gridHeight = maxY - minY + 1;
      
      // If grid is too large, center around current city
      const MAX_SIZE = 15;
      if (gridWidth > MAX_SIZE || gridHeight > MAX_SIZE) {
        // If we have current city coords, center around it
        if (currentCityIncluded) {
          const centerX = cityData.xPosition;
          const centerY = cityData.yPosition;
          
          // REMOVED Math.max(0, ...) to allow negative coordinates
          minX = centerX - Math.floor(MAX_SIZE / 2);
          maxX = minX + MAX_SIZE - 1;
          minY = centerY - Math.floor(MAX_SIZE / 2);
          maxY = minY + MAX_SIZE - 1;
          
          gridWidth = MAX_SIZE;
          gridHeight = MAX_SIZE;
        } 
        // Otherwise just limit the size
        else {
          if (gridWidth > MAX_SIZE) {
            const centerX = Math.floor((minX + maxX) / 2);
            // REMOVED Math.max(0, ...) to allow negative coordinates
            minX = centerX - Math.floor(MAX_SIZE / 2);
            maxX = minX + MAX_SIZE - 1;
            gridWidth = MAX_SIZE;
          }
          
          if (gridHeight > MAX_SIZE) {
            const centerY = Math.floor((minY + maxY) / 2);
            // REMOVED Math.max(0, ...) to allow negative coordinates
            minY = centerY - Math.floor(MAX_SIZE / 2);
            maxY = minY + MAX_SIZE - 1;
            gridHeight = MAX_SIZE;
          }
        }
      }
      
      // Register all cities in our position map
      citiesWithCoords.forEach(city => {
        const x = city.xPosition;
        const y = city.yPosition;
        
        // Only include cities within our grid bounds
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
          const key = `${x}-${y}`;
          if (!cityPositions[key]) {
            cityPositions[key] = [];
          }
          cityPositions[key].push(city);
        }
      });
    }
    
    console.log('Grid dimensions:', gridWidth, 'x', gridHeight);
    console.log('Grid bounds:', `X: ${minX}-${maxX}, Y: ${minY}-${maxY}`);
    console.log('Cities on map:', Object.keys(cityPositions).length);
  
    // Generate the grid cells
    const grid = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const key = `${x}-${y}`;
        const citiesAtPosition = cityPositions[key] || [];
        const isCurrentCityHere = citiesAtPosition.some(city => city.id === cityData.id);
        
        if (citiesAtPosition.length > 0) {
          console.log(`City at ${x},${y}:`, citiesAtPosition.map(c => c.name).join(', '));
        }
        
        grid.push(
          <div 
            key={key} 
            className={`map-cell ${citiesAtPosition.length > 0 ? 'has-city' : ''} ${isCurrentCityHere ? 'current-city-cell' : ''} ${x === 0 && y === 0 ? 'origin-cell' : ''}`}
            style={{
              gridColumn: x - minX + 1,
              gridRow: y - minY + 1
            }}
            title={citiesAtPosition.length > 0 
              ? citiesAtPosition.map(c => `${c.name} (${c.xPosition},${c.yPosition})`).join(', ')
              : `Position ${x},${y}`}
          >
            {citiesAtPosition.length > 0 && (
              <div className={`city-marker ${isCurrentCityHere ? 'current-city-marker' : ''}`}>
                {getCityIcon(citiesAtPosition[0].alignment)}
              </div>
            )}
          </div>
        );
      }
    }
  
    // Update CSS for grid
    document.documentElement.style.setProperty('--map-grid-cols', gridWidth);
    document.documentElement.style.setProperty('--map-grid-rows', gridHeight);
    
    return grid;
  };

  // Default grid when no coordinates are available
  const generateDefaultGrid = () => {
    const gridSize = 10; // Default 10x10 grid
    const grid = [];
    
    // Center the grid at (0,0) instead of starting at (0,0)
    const minX = -5;
    const minY = -5;
    
    // Place the city in the center of the grid
    const centerX = 0;
    const centerY = 0;
  
    for (let y = minY; y < minY + gridSize; y++) {
      for (let x = minX; x < minX + gridSize; x++) {
        const isCity = x === centerX && y === centerY;
        grid.push(
          <div 
            key={`${x}-${y}`} 
            className={`map-cell ${isCity ? 'current-city-cell' : ''} ${x === 0 && y === 0 ? 'origin-cell' : ''}`}
            style={{
              gridColumn: x - minX + 1,
              gridRow: y - minY + 1
            }}
            title={isCity ? `${cityData.name} (0,0)` : `Position ${x},${y}`}
          >
            {isCity && (
              <div className="city-marker current-city-marker">
                {getCityIcon(cityData.alignment)}
              </div>
            )}
          </div>
        );
      }
    }
  
    // Update CSS for grid
    document.documentElement.style.setProperty('--map-grid-cols', gridSize);
    document.documentElement.style.setProperty('--map-grid-rows', gridSize);
  
    return grid;
  };

  return (
    <div className="page-content city-page">
      {/* Show city content only if no building is active */}
      {!activeBuilding ? (
        <>
          {/* City Header */}
          <div className="city-header">
            <div className="city-title">
              <h1>{cityData.name}</h1>
              <span className="city-type">
                {getAlignmentText(cityData.alignment)}
              </span>
            </div>
            <div className="city-actions">
              <Link to="/travel" className="action-button">Travel</Link>
            </div>
          </div>

          {/* City Banner */}
          <div className="city-banner">
            <div className="city-banner-content">
              <div className="city-stats">
                {cityData.owner && (
                  <div className="city-stat owner-stat">
                    <span className="stat-label">Owner</span>
                    <div className="stat-value">
                      {loadingOwner ? (
                        <span className="loading-owner">Loading...</span>
                      ) : ownerData ? (
                        <Link to={`/character/view/${ownerData.id}`} className="owner-link">
                          {ownerData.icon && (
                            <img 
                              src={`${apiConfig.resources}${ownerData.icon}`} 
                              alt={ownerData.username} 
                              className="owner-icon"
                            />
                          )}
                          <span className="owner-name">{ownerData.username}</span>
                        </Link>
                      ) : (
                        <span>{cityData.owner}</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="city-stat">
                  <span className="stat-label">Alignment</span>
                  <span className="stat-value">
                    {getAlignmentText(cityData.alignment)}
                  </span>
                </div>
                {cityData.xPosition !== undefined && cityData.yPosition !== undefined && (
                  <div className="city-stat">
                    <span className="stat-label">Location</span>
                    <span className="stat-value">X: {cityData.xPosition}, Y: {cityData.yPosition}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* City Navigation Tabs */}
          <div className="city-tabs">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'buildings' ? 'active' : ''}`}
              onClick={() => setActiveTab('buildings')}
            >
              Buildings
            </button>
          </div>

          {/* Tab Content */}
          <div className="city-tab-content">
            {activeTab === 'overview' && (
              <div className="city-overview">
                <div className="city-description">
                  <h3>About {cityData.name}</h3>
                  <p>{cityData.description || `${cityData.name} is a ${getAlignmentText(cityData.alignment).toLowerCase()} city.`}</p>
                </div>
                
                <div className="city-location">
                  <h3>Location</h3>
                  <div className="location-map">
                    <div className="map-grid-container">
                      {mapBackground && (
                        <div className="map-background">
                          <img src={mapBackground} alt="Map Background" />
                        </div>
                      )}
                      <div className="map-grid">
                        {generateMapGrid()}
                      </div>
                      <div className="map-legend">
                        <p>Coordinates: 
                          X: {typeof cityData.xPosition === 'number' ? cityData.xPosition : '?'}, 
                          Y: {typeof cityData.yPosition === 'number' ? cityData.yPosition : '?'}
                        </p>
                        <div className="map-legend-items">
                          <div className="map-legend-item">
                            <div className="city-marker current-city-marker">{getCityIcon(cityData.alignment)}</div>
                            <span>You</span>
                          </div>
                          <div className="map-legend-item">
                            <div className="city-marker">•</div>
                            <span>City</span>
                          </div>
                        </div>
                        
                        {/* Update the map background control to show current image */}
                        <div className="map-background-control">
                          <input 
                            type="text" 
                            placeholder="Enter image URL for custom map background" 
                            onChange={(e) => updateMapBackground(e.target.value)}
                            className="map-bg-input"
                          />
                          {mapBackground !== mapImage && mapBackground ? (
                            <button onClick={() => setMapBackground(mapImage)} className="reset-bg-btn">
                              Reset
                            </button>
                          ) : mapBackground ? (
                            <button onClick={() => setMapBackground(null)} className="remove-bg-btn">
                              Remove
                            </button>
                          ) : (
                            <button onClick={() => setMapBackground(mapImage)} className="default-bg-btn">
                              Default
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'buildings' && (
              <div className="city-buildings">
                <h3>City Buildings</h3>
                
                {inJail && (
                  <div className="jail-notice">
                    <p>You are currently imprisoned and can only access the jail.</p>
                  </div>
                )}
                
                <div className="building-card">
                  <h3>City Jail</h3>
                  <p>View prisoners or visit the jail facilities.</p>
                  <button 
                    onClick={() => setActiveBuilding('jail')}
                    className="enter-building-btn"
                  >
                    Enter Jail
                  </button>
                </div>
                
                {!inJail && cityData.buildings && cityData.buildings.length > 0 ? (
                  <div className="buildings-grid">
                    {cityData.buildings.map((building, index) => {
                      const buildingTypeName = getBuildingName(building.type);
                      
                      return (
                        <div className="building-card" key={index}>
                          <div className="building-icon">{getBuildingIcon(building.type)}</div>
                          <div className="building-info">
                            <h4>{building.name || buildingTypeName}</h4>
                            <p className="building-type">{buildingTypeName}</p>
                            <p className="building-description">
                              {building.description || `A ${buildingTypeName.toLowerCase()} in ${cityData.name}.`}
                            </p>
                          </div>
                          <button className="building-action">Visit</button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-buildings">
                    <p>No buildings have been discovered in this city yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        /* Active building shown as full page */
        <div className="active-building-page">
          {activeBuilding === 'jail' ? (
            <CityJail 
              cityId={cityData?.id} 
              cityName={cityData?.name} 
              onBackClick={() => setActiveBuilding(null)} 
            />
          ) : activeBuilding === 'tavern' ? (
            <div className="tavern">
              {/* Tavern content */}
              <div className="building-header">
                <h2>Tavern</h2>
                <button onClick={() => setActiveBuilding(null)} className="back-button">
                  <span className="back-icon">←</span> Back to City
                </button>
              </div>
            </div>
          ) : (
            // Other buildings...
            <div>
              <div className="building-header">
                <h2>Building</h2>
                <button onClick={() => setActiveBuilding(null)} className="back-button">
                  <span className="back-icon">←</span> Back to City
                </button>
              </div>
              Building content here
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default City;
